/**
 * TierGuard
 *
 * Restricts Tier 1 (school-only) accounts from accessing personal features.
 * Must be used inside AuthGuard (assumes user is already authenticated).
 */

import { Navigate, Link as RouterLink } from 'react-router-dom'
import { FiLock } from 'react-icons/fi'
import { useAuthStore, selectIsTier1 } from '../../store'

interface TierGuardProps {
  children: React.ReactNode
  /**
   * Where to redirect if user is Tier 1
   */
  redirectTo?: string
  /**
   * Show a custom message instead of redirecting
   */
  fallback?: React.ReactNode
}

/**
 * Default fallback component for tier-restricted content
 */
function TierRestrictedFallback() {
  return (
    <div className="p-8 max-w-md mx-auto mt-20 text-center bg-background rounded-lg border border-border">
      <div className="flex flex-col items-center gap-4">
        <FiLock className="h-12 w-12 text-muted-foreground" />
        <p className="text-xl font-semibold text-foreground">
          Feature Not Available
        </p>
        <p className="text-muted-foreground">
          This feature is not available for school-only accounts. Contact your school administrator
          for more information.
        </p>
        <RouterLink to="/">
          <button className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md font-medium">
            Go Back Home
          </button>
        </RouterLink>
      </div>
    </div>
  )
}

export function TierGuard({
  children,
  redirectTo,
  fallback,
}: TierGuardProps) {
  const isTier1 = useAuthStore(selectIsTier1)

  if (isTier1) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />
    }
    return <>{fallback ?? <TierRestrictedFallback />}</>
  }

  return <>{children}</>
}
